import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";

const Login = () => {
  const { t } = useLang();
  const { login, finishReset } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Forgot-password modal — two steps: request code, then submit code + new pwd
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1 = ask email, 2 = enter code + pwd
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotCode, setForgotCode] = useState("");
  const [forgotNewPassword, setForgotNewPassword] = useState("");
  const [forgotSending, setForgotSending] = useState(false);

  const openForgot = () => {
    setForgotEmail(email);
    setForgotCode("");
    setForgotNewPassword("");
    setForgotStep(1);
    setForgotOpen(true);
  };
  const closeForgot = () => {
    if (forgotSending) return;
    setForgotOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const cleanEmail = email.trim().toLowerCase();
      await login(cleanEmail, password);
      toast.success(t.auth.login_title);
      navigate("/");
    } catch (err) {
      // Backend returns 403 with detail={code:'email_not_verified', email:...}
      // in that case redirect to the verification screen instead of showing a toast.
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "email_not_verified") {
        toast.message(detail.message || t.auth.verify_email_sent);
        navigate(
          `/verify-email?email=${encodeURIComponent(detail.email || email.trim().toLowerCase())}&notice=verification_required`
        );
        return;
      }
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  // Step 1: request the reset code
  const requestForgotCode = async (e) => {
    e.preventDefault();
    if (forgotSending) return;
    const cleanEmail = forgotEmail.trim().toLowerCase();
    if (!cleanEmail) return;
    setForgotSending(true);
    try {
      await api.post("/auth/forgot-password", { email: cleanEmail });
      toast.success(t.auth.forgot_success);
      setForgotStep(2);
    } catch (err) {
      // If the request reached the server, treat it as success (no enumeration).
      if (err?.response) {
        toast.success(t.auth.forgot_success);
        setForgotStep(2);
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setForgotSending(false);
    }
  };

  // Step 2: submit code + new password
  const submitReset = async (e) => {
    e.preventDefault();
    if (forgotSending) return;
    const cleanEmail = forgotEmail.trim().toLowerCase();
    const code = forgotCode.replace(/\D/g, "").slice(0, 6);
    if (code.length !== 6) {
      toast.error(t.auth.reset_error_code);
      return;
    }
    if (!forgotNewPassword || forgotNewPassword.length < 6) {
      toast.error(t.auth.reset_error_pwd);
      return;
    }
    setForgotSending(true);
    try {
      await finishReset(cleanEmail, code, forgotNewPassword);
      toast.success(t.auth.reset_success);
      setForgotOpen(false);
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setForgotSending(false);
    }
  };

  const resendResetCode = async () => {
    if (forgotSending) return;
    setForgotSending(true);
    try {
      await api.post("/auth/resend-otp", {
        email: forgotEmail.trim().toLowerCase(),
        kind: "reset",
      });
      toast.success(t.auth.forgot_success);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setForgotSending(false);
    }
  };

  return (
    <main className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 py-20" data-testid="login-page">
      <div className="max-w-md mx-auto">
        <span className="overline">Sign in</span>
        <h1 className="font-heading text-5xl mt-4 tracking-tighter text-[#EDE7D8]">
          {t.auth.login_title}
        </h1>

        <form onSubmit={submit} className="mt-10 space-y-6 grid-card p-8" data-testid="login-form">
          <div>
            <label className="field-label">{t.auth.email}</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="field-input"
              data-testid="login-email"
              autoComplete="email"
            />
          </div>
          <div>
            <label className="field-label">{t.auth.password}</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="field-input"
              data-testid="login-password"
              autoComplete="current-password"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-pill w-full justify-center"
            data-testid="login-submit"
          >
            {loading ? t.common.loading : t.auth.submit_login}
          </button>

          <button
            type="button"
            onClick={openForgot}
            className="block mx-auto text-sm text-[#D4AF37] hover:text-[#F1D26B] transition-colors duration-300 underline-offset-4 hover:underline"
            data-testid="login-forgot-password"
          >
            {t.auth.forgot_password}
          </button>
        </form>

        <Link
          to="/register"
          className="mt-6 inline-block text-sm text-[#D4AF37] hover:text-[#F1D26B] transition-colors duration-300"
          data-testid="login-switch-register"
        >
          {t.auth.switch_to_register}
        </Link>
      </div>

      {forgotOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center px-4"
          role="dialog"
          aria-modal="true"
          data-testid="forgot-dialog"
        >
          <div
            className="absolute inset-0 bg-[#0B1437]/85 backdrop-blur-sm"
            onClick={closeForgot}
          />
          {forgotStep === 1 ? (
            <form
              onSubmit={requestForgotCode}
              className="relative grid-card w-full max-w-md p-8 fade-up"
              data-testid="forgot-form-step1"
            >
              <span className="overline">{t.auth.forgot_password}</span>
              <h3 className="font-heading text-3xl mt-3 tracking-tighter text-[#EDE7D8]">
                {t.auth.forgot_title}
              </h3>
              <p className="text-sm text-[#EDE7D8]/70 mt-2">{t.auth.forgot_desc}</p>
              <div className="mt-5">
                <label className="field-label">{t.auth.email}</label>
                <input
                  type="email"
                  required
                  autoFocus
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="field-input"
                  data-testid="forgot-email"
                  autoComplete="email"
                />
              </div>
              <div className="mt-6 flex flex-wrap gap-3 justify-end">
                <button
                  type="button"
                  onClick={closeForgot}
                  className="btn-ghost"
                  disabled={forgotSending}
                  data-testid="forgot-cancel"
                >
                  {t.auth.forgot_cancel}
                </button>
                <button
                  type="submit"
                  className="btn-pill"
                  disabled={forgotSending}
                  data-testid="forgot-submit"
                >
                  {forgotSending ? t.auth.forgot_sending : t.auth.forgot_submit}
                </button>
              </div>
            </form>
          ) : (
            <form
              onSubmit={submitReset}
              className="relative grid-card w-full max-w-md p-8 fade-up"
              data-testid="forgot-form-step2"
            >
              <span className="overline">{t.auth.reset_overline}</span>
              <h3 className="font-heading text-3xl mt-3 tracking-tighter text-[#EDE7D8]">
                {t.auth.reset_title}
              </h3>
              <p className="text-sm text-[#EDE7D8]/70 mt-2">
                {t.auth.reset_desc_prefix}{" "}
                <span className="text-[#D4AF37] font-semibold">{forgotEmail}</span>.
              </p>

              <div className="mt-5">
                <label className="field-label">{t.auth.reset_code_label}</label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  autoFocus
                  maxLength={6}
                  pattern="[0-9]{6}"
                  value={forgotCode}
                  onChange={(e) => setForgotCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="field-input tracking-[0.4em] text-center text-xl"
                  data-testid="forgot-code"
                />
              </div>

              <div className="mt-4">
                <label className="field-label">{t.auth.reset_new_password}</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={forgotNewPassword}
                  onChange={(e) => setForgotNewPassword(e.target.value)}
                  className="field-input"
                  data-testid="forgot-new-password"
                  autoComplete="new-password"
                />
              </div>

              <button
                type="button"
                onClick={resendResetCode}
                disabled={forgotSending}
                className="mt-4 text-xs text-[#D4AF37] hover:text-[#F1D26B] transition-colors duration-300 underline-offset-4 hover:underline disabled:opacity-50"
                data-testid="forgot-resend-code"
              >
                {t.auth.reset_resend}
              </button>

              <div className="mt-6 flex flex-wrap gap-3 justify-end">
                <button
                  type="button"
                  onClick={closeForgot}
                  className="btn-ghost"
                  disabled={forgotSending}
                  data-testid="forgot-cancel-2"
                >
                  {t.auth.forgot_cancel}
                </button>
                <button
                  type="submit"
                  className="btn-pill"
                  disabled={forgotSending}
                  data-testid="forgot-reset-submit"
                >
                  {forgotSending ? t.auth.forgot_sending : t.auth.reset_submit}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </main>
  );
};

export default Login;
